// Raporlar sayfası - Çeşitli raporlar
import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  FiDownload, 
  FiFilter, 
  FiCalendar,
  FiUsers,
  FiFolder,
  FiBarChart2,
  FiLock,
  FiSearch,
  FiX
} from 'react-icons/fi';
import { IoFilterCircleOutline } from 'react-icons/io5';
import { reportService } from '../../services/reportService';
import { userService } from '../../services/userService';
import { projectService } from '../../services/projectService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState('customers');
  const [shouldFetch, setShouldFetch] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [filters, setFilters] = useState({ user: '' });
  const [hasShownDateError, setHasShownDateError] = useState(false);
  const { data: usersData } = useQuery(
    ['users','all'],
    () => userService.getUsers({ page: 1, limit: 1000 }),
    { staleTime: 5 * 60 * 1000 }
  );

  // Rapor türleri
  const reportTypes = [
    {
      id: 'customers',
      name: 'Müşteri Raporu',
      icon: FiUsers
      
    },
    {
      id: 'projects',
      name: 'Proje Raporu',
      icon: FiFolder
    },
    {
      id: 'users',
      name: 'Kullanıcı Raporu',
      icon: FiUsers
    },
    {
      id: 'meetings',
      name: 'Toplantı Raporu',
      icon: FiCalendar
    },
    {
      id: 'statistics',
      name: 'İstatistik Raporu',
      icon: FiBarChart2
    }
    ,
    {
      id: 'offers',
      name: 'Teklif Raporu',
      icon: FiFolder
    }
  ];

  // Seçili raporu al
  const { data: reportData, isLoading, refetch } = useQuery(
    ['report', selectedReport, dateRange, filters],
    () => {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...filters
      };
      if (!filters.user) {
        delete params.user;
      }
      if (!filters.userStatus) {
        delete params.userStatus;
      }
      
      switch (selectedReport) {
        case 'customers':
          return reportService.getCustomerReport(params);
        case 'projects':
          if (filters.status) {
            params.status = filters.status;
          }
          return reportService.getProjectReport(params);
        case 'users':
          return reportService.getUserReport(params);
        case 'meetings':
          return reportService.getMeetingReport(params);
        case 'statistics':
          return reportService.getStatisticsReport(params);
        case 'offers':
          return reportService.getOfferReport(params);
        default:
          return Promise.resolve({});
      }
    },
    { enabled: shouldFetch && !!selectedReport, keepPreviousData: false, cacheTime: 0 }
  );

  // Tarih aralığı değiştirme
  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    setShouldFetch(false);
    setHasShownDateError(false); // Tarih değiştiğinde error state'ini sıfırla
  };

  // Filtre değiştirme
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setShouldFetch(false);
  };

  // Form validasyonu
  const isFormValid = () => {
    return dateRange.startDate && dateRange.endDate;
  };

  // Rapor export
  const handleExport = () => {
    // Export işlemi burada yapılacak
  };

  if (isLoading) {
    return <LoadingSpinner text="Rapor yükleniyor..." />;
  }

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <FiBarChart2 className="mr-2" />
            Raporlar
          </h1>
         
        </div>
        
        <button
          onClick={handleExport}
          className="btn-primary flex items-center"
        >
          <FiDownload className="mr-2" size={16} />
          Export
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
        {/* Rapor türleri */}
        <div className="lg:col-span-1">
          <div className="card p-3">
             <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
               Rapor Türleri
             </h3>
             <div className="space-y-1">
               {reportTypes.map((report) => {
                 const Icon = report.icon;
                 return (
                   <button
                     key={report.id}
                     onClick={() => { setSelectedReport(report.id); setShouldFetch(false); }}
                     className={`w-full text-left p-2 rounded-md transition-colors ${
                       selectedReport === report.id
                         ? 'bg-brand-primary text-white'
                         : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                     }`}
                   >
                     <div className="flex items-center space-x-2">
                       <Icon size={16} />
                       <div>
                         <div className="font-medium text-sm">{report.name}</div>
                         <div className="text-xs opacity-75">{report.description}</div>
                       </div>
                     </div>
                   </button>
                 );
               })}
             </div>
          </div>
        </div>

        {/* Rapor içeriği */}
        <div className="lg:col-span-5">
          <div className="card p-6">
            {/* Rapor başlığı ve filtreler */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {reportTypes.find(r => r.id === selectedReport)?.name}
              </h3>
              <div className="flex items-center space-x-2">
                <FiFilter size={16} className="text-gray-400" />
                <span className="text-sm text-gray-500">Filtreler</span>
              </div>
            </div>

            {/* Kullanıcı + Tarih aralığı filtresi */}
            <div className={`grid grid-cols-1 ${selectedReport === 'users' ? 'md:grid-cols-5' : selectedReport === 'customers' ? 'md:grid-cols-4' : selectedReport === 'projects' ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 mb-6`}>
              {(selectedReport === 'meetings' || selectedReport === 'users') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kullanıcı
                  </label>
                  <select
                    className="input-field w-full"
                    value={filters.user || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, user: e.target.value }))}
                  >
                    <option value="">Tümü</option>
                    {(usersData?.users || []).map((u) => (
                      <option key={u.id} value={u.id}>{u.user_fullname}</option>
                    ))}
                  </select>
                </div>
              )}
              {selectedReport === 'customers' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Durum
                  </label>
                  <select
                    className="input-field w-full"
                    value={filters.customerStatus || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, customerStatus: e.target.value }))}
                  >
                    <option value="">Tümü</option>
                    <option value="1">Aktif</option>
                    <option value="0">Pasif</option>
                  </select>
                </div>
              )}
              {selectedReport === 'projects' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Proje Durumu
                  </label>
                  <select
                    className="input-field w-full"
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">Tümü</option>
                    <option value="Aktif">Aktif</option>
                    <option value="Beklemede">Beklemede</option>
                    <option value="Başarılı Tamamlandı">Başarılı Tamamlandı</option>
                    <option value="Başarısız Tamamlandı">Başarısız Tamamlandı</option>
                    <option value="İptal">İptal</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Başlangıç Tarihi
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bitiş Tarihi
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              {selectedReport === 'users' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Durum
                  </label>
                  <select
                    className="input-field w-full"
                    value={filters.userStatus || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, userStatus: e.target.value }))}
                  >
                    <option value="">Tümü</option>
                    <option value="1">Aktif</option>
                    <option value="0">Pasif</option>
                  </select>
                </div>
              )}
              <div className="flex items-end">
                <button
                  onClick={() => {
                    if (!isFormValid()) {
                      if (!hasShownDateError) {
                        toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
                        setHasShownDateError(true);
                      }
                      return;
                    }
                    setHasShownDateError(false); // Form geçerli olduğunda error state'ini sıfırla
                    setShouldFetch(true);
                    refetch();
                  }}
                  className="btn-primary w-full"
                >
                  Göster
                </button>
              </div>
            </div>

            {/* Rapor içeriği */}
            <div className="space-y-4">

              {!shouldFetch && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Filtreleri seçin ve Göster butonuna basın.
                </div>
              )}

              {selectedReport === 'customers' && shouldFetch && (
                <CustomerReportContent data={reportData} />
              )}
              
              {selectedReport === 'projects' && shouldFetch && (
                <ProjectReportContent data={reportData} />
              )}
              
              {selectedReport === 'users' && shouldFetch && (
                <UserReportContent data={reportData} />
              )}
              
              {selectedReport === 'meetings' && shouldFetch && (
                <MeetingReportContent data={reportData} />
              )}
              
              {selectedReport === 'statistics' && shouldFetch && (
                <StatisticsReportContent data={reportData} />
              )}

              {selectedReport === 'offers' && shouldFetch && (
                <OfferReportContent data={reportData} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Müşteri raporu içeriği
const CustomerReportContent = ({ data }) => {
  const customers = data?.customers || [];
  
  return (
    <div>
      <div className="mb-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
          Müşteri Listesi ({customers.length} kayıt)
        </h4>
      </div>
      
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Müşteri Adı</th>
              <th>Telefon</th>
              <th>Hesap Yöneticisi</th>
              <th>Durum</th>
              <th>Oluşturulma</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.id}>
                <td className="font-medium">{customer.customer_name}</td>
                <td>{customer.customer_phone}</td>
                <td>{customer.customer_account_manager}</td>
                <td>
                  <span className={customer.cutomer_status ? 'status-active' : 'status-inactive'}>
                    {customer.cutomer_status ? 'Aktif' : 'Pasif'}
                  </span>
                </td>
                <td>{new Date(customer.created_at).toLocaleDateString('tr-TR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Proje raporu içeriği
const ProjectReportContent = ({ data }) => {
  const projects = data?.projects || [];
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [filters, setFilters] = useState({ name: '', customer: '', status: '', assigned_to: [], opportunity: [], close_date: '' });
  const [tempFilters, setTempFilters] = useState({ name: '', customer: '', assigned_to: [], opportunity: [], close_date: '' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilters, setShowFilters] = useState({ name: false, customer: false, status: false, assigned_to: false, opportunity: false, close_date: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Fırsat değerini rakam + metin karşılığına çeviren fonksiyon
  const getOpportunityText = (value) => {
    switch (value) {
      case '0': return '0 (Henüz belli değil)';
      case '1': return '1 (Çok Kötü)';
      case '2': return '2 (Kötü)';
      case '3': return '3 (Orta)';
      case '4': return '4 (İyi)';
      case '5': return '5 (Çok İyi)';
      default: return value;
    }
  };

  // Proje atanmış kullanıcıları al
  const { data: assigneesData } = useQuery(
    ['projectAssignees', searchTerm, filters.status, filters.customer, filters.name, filters.opportunity],
    () => projectService.getProjectAssignees({
      search: searchTerm,
      status: filters.status,
      customer: filters.customer,
      name: filters.name,
      opportunity: (filters.opportunity && filters.opportunity.length > 0) ? filters.opportunity.join(',') : ''
    }),
    { keepPreviousData: true }
  );

  // Filtreleme ve sıralama
  const filteredAndSorted = useMemo(() => {
    let list = [...projects];
    if (searchTerm) list = list.filter(p => (p.project_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (p.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()));
    if (filters.name) list = list.filter(p => (p.project_name || '').toLowerCase().includes(filters.name.toLowerCase()));
    if (filters.customer) list = list.filter(p => (p.customer_name || '').toLowerCase().includes(filters.customer.toLowerCase()));
    if (filters.status) list = list.filter(p => String(p.project_status) === String(filters.status));
    if (filters.assigned_to && filters.assigned_to.length > 0) list = list.filter(p => filters.assigned_to.includes(String(p.assigned_to)));
    if (filters.opportunity && filters.opportunity.length > 0) list = list.filter(p => filters.opportunity.includes(String(p.project_opportunity)));
    if (filters.close_date) {
      const filterDate = new Date(filters.close_date).toISOString().split('T')[0];
      list = list.filter(p => {
        if (!p.close_date) return false;
        const projectDate = new Date(p.close_date).toISOString().split('T')[0];
        return projectDate === filterDate;
      });
    }
    if (sortConfig.key) {
      list.sort((a, b) => {
        if (sortConfig.key === 'latest_offer_ratio') {
          const aTotal = Number(a.latest_offer_total || 0);
          const bTotal = Number(b.latest_offer_total || 0);
          const aProfit = Number(a.latest_profitability || 0);
          const bProfit = Number(b.latest_profitability || 0);
          const av = aTotal > 0 ? (aProfit / aTotal) : -Infinity;
          const bv = bTotal > 0 ? (bProfit / bTotal) : -Infinity;
          if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
          if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
        const numericKeys = new Set(['latest_offer_total', 'latest_profitability']);
        const dateKeys = new Set(['created_at', 'close_date']);
        const avRaw = a[sortConfig.key];
        const bvRaw = b[sortConfig.key];
        let av, bv;
        if (dateKeys.has(sortConfig.key)) {
          av = avRaw ? new Date(avRaw).getTime() : 0;
          bv = bvRaw ? new Date(bvRaw).getTime() : 0;
        } else if (numericKeys.has(sortConfig.key)) {
          av = Number(avRaw || 0);
          bv = Number(bvRaw || 0);
        } else {
          av = (avRaw ?? '').toString().toLowerCase();
          bv = (bvRaw ?? '').toString().toLowerCase();
        }
        if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [projects, filters, sortConfig, searchTerm]);

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const toggleFilter = (key) => setShowFilters(prev => ({ ...prev, [key]: !prev[key] }));

  const handleFilterChange = (type, value) => {
    setFilters(prev => ({ ...prev, [type]: value }));
  };

  const handleTempFilterChange = (type, value) => {
    setTempFilters(prev => ({ ...prev, [type]: value }));
  };

  const handleFilterKeyDown = (type, e) => {
    if (e.key === 'Enter') {
      setFilters(prev => ({ ...prev, [type]: tempFilters[type] }));
      setShowFilters(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchTerm(tempSearchTerm);
    }
  };

  const hasActiveFilters = () => {
    return searchTerm || filters.name || filters.customer || filters.status || (filters.assigned_to && filters.assigned_to.length > 0) || (filters.opportunity && filters.opportunity.length > 0) || filters.close_date;
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setTempSearchTerm('');
    setFilters({ name: '', customer: '', status: '', assigned_to: [], opportunity: [], close_date: '' });
    setTempFilters({ name: '', customer: '', assigned_to: [], opportunity: [], close_date: '' });
    setShowFilters({ name: false, customer: false, status: false, assigned_to: false, opportunity: false, close_date: false });
  };

  const formatCurrency = (amount, currency) => {
    const cur = currency || 'TRY';
    const currencyCode = cur === 'TL' ? 'TRY' : cur;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(amount || 0));
  };

  // Sayfalama için filtreleme ve sıralama sonrası listeyi böl
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSorted.slice(startIndex, endIndex);
  }, [filteredAndSorted, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);

  // Mevcut tablodaki projelerin toplam tutarları - para birimine göre ayrı
  const tableStats = useMemo(() => {
    const init = {
      TRY: { amount: 0, profit: 0, count: 0 },
      USD: { amount: 0, profit: 0, count: 0 },
      totalCount: 0,
    };
    if (!filteredAndSorted || filteredAndSorted.length === 0) return init;

    return filteredAndSorted.reduce((agg, project) => {
      const curRaw = (project.latest_offer_currency || 'TRY');
      const key = curRaw === 'USD' ? 'USD' : (curRaw === 'TL' ? 'TRY' : 'TRY');
      const amount = Number(project.latest_offer_total || 0);
      const profit = Number(project.latest_profitability || 0);
      
      // Sadece tutarı olan projeleri say (tutar > 0)
      if (amount > 0) {
        agg[key].amount += amount;
        agg[key].profit += profit;
        agg[key].count += 1;
        agg.totalCount += 1;
      }
      
      return agg;
    }, init);
  }, [filteredAndSorted]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };
  
  return (
    <div className="space-y-4">
      {/* Arama */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FiSearch className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Proje ara... (Enter ile ara)"
          value={tempSearchTerm}
          onChange={(e) => setTempSearchTerm(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className="input-field pl-10"
        />
      </div>

      {/* Filtre Uyarı Mesajı */}
      {hasActiveFilters() && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Filtre uygulanmıştır
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  {searchTerm && `Arama: "${searchTerm}"`}
                  {filters.name && ` • Proje Adı: "${filters.name}"`}
                  {filters.customer && ` • Müşteri: "${filters.customer}"`}
                  {filters.status && ` • Durum: "${filters.status}"`}
                  {filters.assigned_to && filters.assigned_to.length > 0 && ` • Account Manager: ${filters.assigned_to.length} seçildi`}
                  {filters.opportunity && filters.opportunity.length > 0 && ` • Fırsat: ${filters.opportunity.length} seçildi`}
                  {filters.close_date && ` • Kapanma: ${new Date(filters.close_date).toLocaleDateString('tr-TR')}`}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700"
              >
                <FiX className="mr-1" size={14} />
                Tüm Filtreleri Temizle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tablo */}
      <div className="card">
        <div className="table-container overflow-x-auto">
          <table className="table w-full table-fixed text-xs projects-table">
            <thead>
              <tr>
                <th className="relative w-[12%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('customer_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      MÜŞTERİ ADI
                      {sortConfig.key === 'customer_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('customer')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.customer && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="text" 
                        placeholder="Filtrele... (Enter ile uygula)" 
                        value={tempFilters.customer} 
                        onChange={(e) => handleTempFilterChange('customer', e.target.value)} 
                        onKeyDown={(e) => handleFilterKeyDown('customer', e)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="relative w-[15%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('project_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      PROJE ADI
                      {sortConfig.key === 'project_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('name')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.name && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="text" 
                        placeholder="Filtrele... (Enter ile uygula)" 
                        value={tempFilters.name} 
                        onChange={(e) => handleTempFilterChange('name', e.target.value)} 
                        onKeyDown={(e) => handleFilterKeyDown('name', e)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="relative w-[10%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('assigned_to_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      ACCOUNT MANAGER
                      {sortConfig.key === 'assigned_to_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('assigned_to')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.assigned_to && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2 min-w-[200px]">
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {assigneesData?.assignees && assigneesData.assignees.length > 0 ? (
                          assigneesData.assignees.map((user) => (
                            <label key={user.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.assigned_to.includes(String(user.id))}
                                onChange={() => handleFilterChange('assigned_to', filters.assigned_to.includes(String(user.id)) ? filters.assigned_to.filter(v=>v!==String(user.id)) : [...filters.assigned_to, String(user.id)])}
                                className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">{user.user_fullname}</span>
                            </label>
                          ))
                        ) : (
                          <div className="text-xs text-gray-500 dark:text-gray-400 p-2">Listelenecek kullanıcı yok</div>
                        )}
                      </div>
                      {filters.assigned_to.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => handleFilterChange('assigned_to', [])}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Tümünü Temizle
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </th>
                <th className="w-[8%]">
                  <button onClick={()=>handleSort('latest_offer_total')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                    PROJE TUTARI
                    {sortConfig.key === 'latest_offer_total' && (
                      <span className="ml-1">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="w-[8%]">
                  <button onClick={()=>handleSort('latest_profitability')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                    KARLILIK
                    {sortConfig.key === 'latest_profitability' && (
                      <span className="ml-1">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="w-[8%]">
                  <button onClick={()=>handleSort('latest_offer_ratio')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                    KARLILIK (%)
                    {sortConfig.key === 'latest_offer_ratio' && (
                      <span className="ml-1">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="relative w-[8%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('project_status')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      PROJE DURUMU
                      {sortConfig.key === 'project_status' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('status')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.status && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <select value={filters.status} onChange={(e)=>handleFilterChange('status', e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded">
                        <option value="">Tümü</option>
                        <option value="Aktif">Aktif</option>
                        <option value="Beklemede">Beklemede</option>
                        <option value="Başarılı Tamamlandı">Başarılı Tamamlandı</option>
                        <option value="Başarısız Tamamlandı">Başarısız Tamamlandı</option>
                        <option value="İptal">İptal</option>
                      </select>
                    </div>
                  )}
                </th>
                <th className="relative w-[6%]">
                  <div className="flex items-center justify-between">
                    <span>Fırsat</span>
                    <button onClick={()=>toggleFilter('opportunity')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.opportunity && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2 min-w-[200px]">
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {['0','1','2','3','4','5'].map((val) => (
                          <label key={val} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filters.opportunity.includes(val)}
                              onChange={() => handleFilterChange('opportunity', filters.opportunity.includes(val) ? filters.opportunity.filter(v=>v!==val) : [...filters.opportunity, val])}
                              className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="text-xs text-gray-700 dark:text-gray-300">{getOpportunityText(val)}</span>
                          </label>
                        ))}
                      </div>
                      {filters.opportunity.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => handleFilterChange('opportunity', [])}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Tümünü Temizle
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </th>
                <th className="relative w-[8%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('created_at')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      OLUŞTURULMA
                      {sortConfig.key === 'created_at' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </div>
                </th>
                <th className="relative w-[8%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('close_date')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      KAPANMA
                      {sortConfig.key === 'close_date' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('close_date')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.close_date && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="date" 
                        placeholder="Tarih seçin" 
                        value={tempFilters.close_date} 
                        onChange={(e) => handleTempFilterChange('close_date', e.target.value)} 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleFilterChange('close_date', tempFilters.close_date);
                            setShowFilters(prev => ({ ...prev, close_date: false }));
                          }
                        }}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedProjects.length > 0 ? (
                paginatedProjects.map((project) => (
                  <tr key={project.id} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700" onClick={()=> window.location.href = `/projects/${project.project_uuid}`}>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {project.customer_name}
                    </td>
                    <td>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white flex items-center">
                          {project.project_visibility === 'Private' && (
                            <FiLock className="mr-2 text-red-500" size={14} />
                          )}
                          {project.project_name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {project.project_description}
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {project.assigned_to_name || '-'}
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      <div className="relative group inline-block">
                        {(() => {
                          const currency = (project.latest_offer_currency || 'TRY') === 'USD' ? 'USD' : 'TRY';
                          const total = Number(project.latest_offer_total || 0);
                          return total > 0
                            ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(total)
                            : '-';
                        })()}
                        {project.latest_offers_info && (
                          <div className="absolute z-10 hidden group-hover:block left-0 mt-1 w-64 p-3 rounded-md bg-gray-900 text-white text-xs shadow-lg">
                            <div className="font-medium mb-2">Geçmiş Teklifler</div>
                            <div className="space-y-1 max-h-64 overflow-auto">
                              {project.latest_offers_info.split(';').map((row, idx) => {
                                const [dateStr, totalStr, curr] = row.split('|');
                                const currCode = (curr || 'TRY') === 'USD' ? 'USD' : 'TRY';
                                const formatted = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currCode }).format(Number(totalStr || 0));
                                return (
                                  <div key={idx} className="flex items-center justify-between">
                                    <span className="opacity-80">{dateStr}</span>
                                    <span className="ml-3">{formatted}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {(() => {
                        const currency = (project.latest_offer_currency || 'TRY') === 'USD' ? 'USD' : 'TRY';
                        const profit = Number(project.latest_profitability || 0);
                        return profit > 0
                          ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(profit)
                          : '-';
                      })()}
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {(() => {
                        const total = Number(project.latest_offer_total || 0);
                        const profit = Number(project.latest_profitability || 0);
                        return total > 0 ? `${((profit / total) * 100).toFixed(2)}%` : '-';
                      })()}
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        project.project_status === 'Aktif' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        project.project_status === 'Beklemede' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        project.project_status === 'Başarılı Tamamlandı' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        project.project_status === 'Başarısız Tamamlandı' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        project.project_status === 'İptal' ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {project.project_status}
                      </span>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {getOpportunityText(project.project_opportunity)}
                    </td>
                    <td className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(project.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="text-sm text-gray-500 dark:text-gray-400">
                      {project.close_date ? new Date(project.close_date).toLocaleDateString('tr-TR') : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <FiFolder size={48} className="text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400">Henüz proje bulunmuyor</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sayfalama */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {/* Sol: Show records */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">records</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                {filteredAndSorted.length} kayıt bulundu
              </div>
            </div>
            
            {/* Sağ: Para birimine göre toplam tutarlar */}
            <div className="w-full flex justify-end">
              <div className="inline-block min-w-[420px] max-w-[560px] w-full sm:w-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                {/* Başlık satırı */}
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 rounded-t-lg">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Toplamlar</span>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {/* TRY satırı */}
                  <div className="flex items-center justify-between px-4 py-3">
                    {tableStats.TRY.count > 0 ? (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-700">TL</span>
                        <div className="flex-1 flex items-center justify-end gap-6 text-sm">
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Proje Tutarı:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.TRY.amount, 'TRY')}</span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Kârlılık:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.TRY.profit, 'TRY')}</span>
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({tableStats.TRY.amount > 0 ? ((tableStats.TRY.profit / tableStats.TRY.amount) * 100).toFixed(1) : '0.0'}%)</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full text-sm font-medium text-red-600 dark:text-red-400">TL proje bulunamadı</div>
                    )}
                  </div>
                  {/* USD satırı */}
                  <div className="flex items-center justify-between px-4 py-3">
                    {tableStats.USD.count > 0 ? (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700">USD</span>
                        <div className="flex-1 flex items-center justify-end gap-6 text-sm">
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Proje Tutarı:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.USD.amount, 'USD')}</span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Kârlılık:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.USD.profit, 'USD')}</span>
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({tableStats.USD.amount > 0 ? ((tableStats.USD.profit / tableStats.USD.amount) * 100).toFixed(1) : '0.0'}%)</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full text-sm font-medium text-red-600 dark:text-red-400">USD proje bulunamadı</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sayfa navigasyonu */}
          {totalPages > 1 && (
            <div className="flex items-center justify-end mt-4">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sayfa {currentPage} / {totalPages}
                </span>
                <div className="flex space-x-1">
                  {/* İlk sayfa */}
                  <button
                    onClick={() => handlePageChange(1)}
                    className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                      currentPage === 1
                        ? 'bg-brand-primary text-white shadow-lg'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                    }`}
                  >
                    1
                  </button>
                  
                  {/* Sol ellipsis */}
                  {currentPage > 4 && (
                    <span className="px-2 py-2 text-sm text-gray-500 font-medium">...</span>
                  )}
                  
                  {/* Orta sayfalar */}
                  {Array.from({ length: 3 }, (_, i) => {
                    let pageNum = currentPage - 1 + i;
                    if (pageNum < 2) pageNum = 2;
                    if (pageNum > totalPages - 1) pageNum = totalPages - 1;
                    if (pageNum === 1 || pageNum === totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                          currentPage === pageNum
                            ? 'bg-brand-primary text-white shadow-lg'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  }).filter(Boolean)}
                  
                  {/* Sağ ellipsis */}
                  {currentPage < totalPages - 3 && (
                    <span className="px-2 py-2 text-sm text-gray-500 font-medium">...</span>
                  )}
                  
                  {/* Son sayfa (1'den farklıysa) */}
                  {totalPages > 1 && (
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                        currentPage === totalPages
                          ? 'bg-brand-primary text-white shadow-lg'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                      }`}
                    >
                      {totalPages}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Uyarı mesajı */}
        {tableStats.totalCount > 0 && (
          <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-600 dark:text-blue-300 text-center">
              <span className="font-medium">Not:</span> Yukarıdaki tutarlar sadece mevcut tabloda görünen {tableStats.totalCount} projeye aittir. 
              Filtreleme veya sayfa değişikliği yapıldığında bu tutarlar otomatik olarak güncellenir. Tüm kayıtlara ait değildir.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Kullanıcı raporu içeriği
const UserReportContent = ({ data }) => {
  const users = data?.users || [];
  
  return (
    <div>
      <div className="mb-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
          Kullanıcı Listesi ({users.length} kayıt)
        </h4>
      </div>
      
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Ad Soyad</th>
              <th>Yıllık Müşteri Hedefi</th>
              <th>Yıllık Kârlılık Hedefi</th>
              <th>Oluşturulan Toplantılar</th>
              <th>Atanan Projeler</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="font-medium text-gray-900 dark:text-white">{user.user_fullname}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                      {user.user_department || '-'}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${user.user_status ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                      {user.user_status ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="text-sm">
                    <div className="font-medium text-gray-900 dark:text-white">
                      Hedef: {user.yearly_customer_target || '-'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Atanan: {user.assigned_customers_count || 0} müşteri
                    </div>
                  </div>
                </td>
                <td>
                  <div className="text-sm">
                    <div className="font-medium text-gray-900 dark:text-white">
                      Hedef: {user.yearly_profitability_target ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: user.yearly_profitability_currency || 'TRY' }).format(Number(user.yearly_profitability_target)) : '-'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Gerçekleşen: {user.total_profit_achieved ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Number(user.total_profit_achieved)) : '₺0,00'}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {user.meetings_created_count || 0}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {user.projects_assigned_count || 0}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Toplantı raporu içeriği
const MeetingReportContent = ({ data }) => {
  const meetings = data?.meetings || [];
  
  return (
    <div>
      <div className="mb-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
          Toplantı Listesi ({meetings.length} kayıt)
        </h4>
      </div>
      
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Konu</th>
              <th>Proje</th>
              <th>Müşteri</th>
              <th>Toplantı Sahibi</th>
              <th>Katılımcılar</th>
              <th>Başlangıç</th>
              <th>Bitiş</th>
              <th>Durum</th>
              <th>İptal/Ertelenme Sebebi</th>
            </tr>
          </thead>
          <tbody>
            {meetings.map((meeting) => (
              <tr key={meeting.id}>
                <td className="font-medium">{meeting.meeting_subject}</td>
                <td>{meeting.project_name}</td>
                <td>{meeting.customer_name}</td>
                <td>{meeting.created_by_name || '-'}</td>
                <td>
                  {(() => {
                    if (!meeting.meeting_participation) return '-';
                    try {
                      const participants = JSON.parse(meeting.meeting_participation);
                      if (!Array.isArray(participants) || participants.length === 0) return '-';
                      return (
                        <div className="flex flex-wrap gap-1">
                          {participants.map((p, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                              {p.user_fullname || p.fullname || p.name || 'Kullanıcı'}
                            </span>
                          ))}
                        </div>
                      );
                    } catch {
                      return '-';
                    }
                  })()}
                </td>
                <td>{new Date(meeting.meeting_start_date).toLocaleString('tr-TR')}</td>
                <td>{new Date(meeting.meeting_end_date).toLocaleString('tr-TR')}</td>
                <td>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    meeting.meeting_status === 'Tamamlandı' ? 'bg-green-100 text-green-800' :
                    meeting.meeting_status === 'Planlandı' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {meeting.meeting_status}
                  </span>
                </td>
                <td className="text-sm text-gray-700 dark:text-gray-300">
                  {['İptal', 'Ertelendi'].includes(meeting.meeting_status) && meeting.meeting_status_description
                    ? meeting.meeting_status_description
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// İstatistik raporu içeriği
const StatisticsReportContent = ({ data }) => {
  const stats = data || {};
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="card p-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Müşteri İstatistikleri
        </h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Toplam Müşteri:</span>
            <span className="font-medium">{stats.customerStats?.total_customers || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Aktif Müşteri:</span>
            <span className="font-medium text-green-600">{stats.customerStats?.active_customers || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Pasif Müşteri:</span>
            <span className="font-medium text-red-600">{stats.customerStats?.inactive_customers || 0}</span>
          </div>
        </div>
      </div>

      <div className="card p-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Proje İstatistikleri
        </h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Toplam Proje:</span>
            <span className="font-medium">{stats.projectStats?.total_projects || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Aktif Proje:</span>
            <span className="font-medium text-green-600">{stats.projectStats?.active_projects || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Tamamlanan:</span>
            <span className="font-medium text-blue-600">{stats.projectStats?.completed_projects || 0}</span>
          </div>
        </div>
      </div>

      <div className="card p-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Toplantı İstatistikleri
        </h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Toplam Toplantı:</span>
            <span className="font-medium">{stats.meetingStats?.total_meetings || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Tamamlanan:</span>
            <span className="font-medium text-green-600">{stats.meetingStats?.completed_meetings || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Planlanan:</span>
            <span className="font-medium text-yellow-600">{stats.meetingStats?.planned_meetings || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Teklif raporu içeriği
const OfferReportContent = ({ data }) => {
  const offers = data?.offers || [];

  return (
    <div>
      <div className="mb-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
          Teklif Listesi ({offers.length} kayıt)
        </h4>
      </div>

      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Teklif</th>
              <th>Durum</th>
              <th>Müşteri</th>
              <th>Proje</th>
              <th>Tarih</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((offer) => (
              <tr key={offer.id}>
                <td className="font-medium">{offer.offer_file_name}</td>
                <td>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    offer.offer_status === 'Teklif Onaylandı' ? 'bg-green-100 text-green-800' :
                    offer.offer_status === 'Teklif Kabul Edilmedi' ? 'bg-red-100 text-red-800' :
                    offer.offer_status === 'Gönderildi' ? 'bg-blue-100 text-blue-800' :
                    offer.offer_status === 'Müşteri Onay Sürecinde' ? 'bg-yellow-100 text-yellow-800' :
                    offer.offer_status === 'Müşteri Revize İstedi' ? 'bg-purple-100 text-purple-800' :
                    offer.offer_status === 'Revize Gönderilecek' ? 'bg-indigo-100 text-indigo-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {offer.offer_status}
                  </span>
                </td>
                <td>{offer.customer_name}</td>
                <td>{offer.project_name}</td>
                <td>{new Date(offer.updated_at).toLocaleDateString('tr-TR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Reports;
